import { useState, useEffect, useRef } from "react";
import {
    ArchiveBoxIcon, UsersIcon, ClipboardDocumentListIcon, SparklesIcon,
    MusicalNoteIcon, DocumentTextIcon, CalendarDaysIcon, TrashIcon, EyeIcon, MicrophoneIcon,
} from "@heroicons/react/24/outline";
import Spinner from "../components/Spinner";

interface ReunionArchivee {
    id: number; titre: string; date: string; participants: string[];
    ordresduJour: string[]; transcription: string; statutTranscription: string;
    urlAudio: string; urlCompteRendu: string;
}

export default function Archives() {
    const [reunions, setReunions] = useState<ReunionArchivee[]>([]);
    const [chargement, setChargement] = useState(true);
    const [erreur, setErreur] = useState("");
    const [succes, setSucces] = useState("");
    const [reunionSelectionnee, setReunionSelectionnee] = useState<ReunionArchivee | null>(null);
    const [uploadEnCours, setUploadEnCours] = useState<number | null>(null);
    const [reunionAudioId, setReunionAudioId] = useState<number | null>(null);
    const [telechargementCRC, setTelechargementCRC] = useState<number | null>(null);
    const refInputAudio = useRef<HTMLInputElement>(null);

    const recupererArchives = async () => {
        try {
            const reponse = await fetch(
                `${process.env.REACT_APP_API_URL}/api/reunions/archives`,
                { credentials: "include", headers: { "Content-Type": "application/json" } }
            );
            if (!reponse.ok) throw new Error();
            setReunions(await reponse.json());
        } catch {
            setErreur("Impossible de charger les archives");
        } finally {
            setChargement(false);
        }
    };

    useEffect(() => {
        recupererArchives();
        const intervalle = setInterval(recupererArchives, 10000);
        return () => clearInterval(intervalle);
    }, []);

    const supprimerReunion = async (reunion: ReunionArchivee) => {
        const transcriptionEnCours = reunion.statutTranscription === "en_cours";
        const message = transcriptionEnCours
            ? `⚠️ Une transcription est en cours pour "${reunion.titre}".\n\nSi vous supprimez cette réunion, la transcription sera annulée et toutes les données seront perdues.\n\nConfirmez-vous la suppression ?`
            : `Supprimer la réunion "${reunion.titre}" ? Cette action est irréversible.`;

        if (!window.confirm(message)) return;

        try {
            const reponse = await fetch(
                `${process.env.REACT_APP_API_URL}/api/reunions/${reunion.id}`,
                { method: "DELETE", credentials: "include" }
            );
            if (!reponse.ok) throw new Error();
            setReunions(prev => prev.filter(r => r.id !== reunion.id));
            if (reunionSelectionnee?.id === reunion.id) setReunionSelectionnee(null);
            setSucces("Réunion supprimée");
            setTimeout(() => setSucces(""), 3000);
        } catch { setErreur("Impossible de supprimer la réunion"); }
    };

    const ouvrirAudio = (id: number) => {
        setReunionAudioId(id);
        setTimeout(() => refInputAudio.current?.click(), 100);
    };

    const uploaderAudio = async (fichier: File) => {
        if (!reunionAudioId) return;
        setUploadEnCours(reunionAudioId);
        setErreur("");
        try {
            const donnees = new FormData();
            donnees.append("fichier", fichier);
            const reponse = await fetch(
                `${process.env.REACT_APP_API_URL}/api/audio/uploader/${reunionAudioId}`,
                { method: "POST", credentials: "include", body: donnees }
            );
            if (!reponse.ok) throw new Error();
            setSucces("Audio uploadé ! Transcription en cours...");
            setTimeout(() => setSucces(""), 6000);
            await recupererArchives();
        } catch { setErreur("Impossible d'uploader l'audio"); }
        finally {
            setUploadEnCours(null);
            setReunionAudioId(null);
        }
    };

    const telechargerCRC = async (id: number, titre: string) => {
        setTelechargementCRC(id);
        try {
            const reponse = await fetch(
                `${process.env.REACT_APP_API_URL}/api/reunions/${id}/generer-crc`,
                { credentials: "include" }
            );
            if (!reponse.ok) throw new Error();
            const blob = await reponse.blob();
            const url = URL.createObjectURL(blob);
            const lien = document.createElement("a");
            lien.href = url;
            lien.download = `CRC-${titre}.docx`;
            lien.click();
            URL.revokeObjectURL(url);
        } catch { setErreur("Impossible de générer le compte-rendu"); }
        finally { setTelechargementCRC(null); }
    };

    const telechargerAudio = (url: string, titre: string) => {
        const lien = document.createElement("a");
        lien.href = url; lien.download = `${titre}_audio.mp3`; lien.click();
    };

    const badgeTranscription = (statut: string) => {
        if (statut === "terminee") return "bg-green-100 text-green-700";
        if (statut === "en_cours") return "bg-orange-100 text-orange-700";
        if (statut === "erreur") return "bg-red-100 text-red-700";
        return "bg-gray-100 text-gray-500";
    };

    const labelTranscription = (statut: string) => {
        if (statut === "terminee") return "Transcrite";
        if (statut === "en_cours") return "En cours...";
        if (statut === "erreur") return "Erreur";
        return "Non transcrite";
    };

    return (
        <div className="space-y-6">
            <input type="file" accept="audio/*" className="hidden" ref={refInputAudio}
                onChange={e => { const f = e.target.files?.[0]; if (f) uploaderAudio(f); e.target.value = ""; }} />

            {uploadEnCours !== null && (
                <div className="fixed inset-0 flex items-center justify-center"
                    style={{ zIndex: 9998, backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-4 shadow-2xl">
                        <Spinner size="lg" className="text-purple-600" />
                        <p className="font-semibold text-gray-800">Upload en cours...</p>
                        <p className="text-gray-400 text-sm">Veuillez patienter</p>
                    </div>
                </div>
            )}

            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800">Archives</h1>
                    <p className="text-gray-400 mt-1">Historique de toutes les réunions passées</p>
                </div>
                <div className="px-4 py-2 rounded-xl text-white text-sm font-medium"
                    style={{ background: "linear-gradient(90deg, #6B21A8, #F97316)" }}>
                    {reunions.length} réunion(s)
                </div>
            </div>

            {erreur && <div className="alert alert-error rounded-xl"><span>{erreur}</span></div>}
            {succes && <div className="alert alert-success rounded-xl"><span>{succes}</span></div>}

            {chargement ? (
                <div className="flex justify-center items-center h-64">
                    <Spinner size="lg" className="text-purple-600" />
                </div>
            ) : (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-2 h-6 rounded-full"
                            style={{ background: "linear-gradient(#6B21A8, #F97316)" }}></div>
                        <h2 className="text-xl font-bold text-gray-800">Réunions archivées</h2>
                    </div>
                    {reunions.length === 0 ? (
                        <div className="text-center py-16">
                            <ArchiveBoxIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-400 font-medium">Aucune réunion archivée</p>
                            <p className="text-gray-300 text-sm mt-1">Les réunions terminées apparaîtront ici</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-100">
                                        <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Titre</th>
                                        <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Date</th>
                                        <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Participants</th>
                                        <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Transcription</th>
                                        <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reunions.map(reunion => (
                                        <tr key={reunion.id}
                                            className="border-b border-gray-50 hover:bg-purple-50/30 transition-colors">
                                            <td className="py-4 px-4 font-medium text-gray-800">{reunion.titre}</td>
                                            <td className="py-4 px-4 text-gray-500 text-sm">
                                                {new Date(reunion.date).toLocaleDateString("fr-FR")}
                                            </td>
                                            <td className="py-4 px-4">
                                                <span className="bg-purple-100 text-purple-700 text-xs font-medium px-2 py-1 rounded-lg">
                                                    {reunion.participants.length} participant(s)
                                                </span>
                                            </td>
                                            <td className="py-4 px-4">
                                                <span className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg w-fit ${badgeTranscription(reunion.statutTranscription)}`}>
                                                    {reunion.statutTranscription === "en_cours" && <Spinner size="xs" className="mr-1" />}
                                                    {labelTranscription(reunion.statutTranscription)}
                                                </span>
                                            </td>
                                            <td className="py-4 px-4">
                                                <div className="flex gap-2 flex-row">
                                                    <button className="btn btn-sm rounded-xl text-purple-600 border-purple-200 hover:bg-purple-50 gap-1"
                                                        onClick={() => setReunionSelectionnee(reunion)}>
                                                        <EyeIcon className="w-4 h-4" />Détail
                                                    </button>
                                                    <button className="btn btn-sm rounded-xl text-orange-500 border-orange-200 hover:bg-orange-50 gap-1"
                                                        onClick={() => ouvrirAudio(reunion.id)}
                                                        disabled={uploadEnCours === reunion.id}>
                                                        <MicrophoneIcon className="w-4 h-4" />Audio
                                                    </button>
                                                    <button className="btn btn-sm rounded-xl text-red-500 border-red-200 hover:bg-red-50 gap-1"
                                                        onClick={() => supprimerReunion(reunion)}>
                                                        <TrashIcon className="w-4 h-4" />Supprimer
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {reunionSelectionnee && (
                <div className="fixed inset-0 flex items-center justify-center"
                    style={{ zIndex: 9999, backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="bg-white max-w-3xl w-full mx-4 rounded-2xl p-6 max-h-screen overflow-y-auto">
                        <div className="flex items-start justify-between mb-6">
                            <div>
                                <h3 className="font-bold text-xl text-gray-800">{reunionSelectionnee.titre}</h3>
                                <div className="flex items-center gap-1 text-gray-400 text-sm mt-1">
                                    <CalendarDaysIcon className="w-4 h-4" />
                                    {new Date(reunionSelectionnee.date).toLocaleDateString("fr-FR")}
                                </div>
                            </div>
                            <span className="px-3 py-1 rounded-xl text-white text-xs font-medium"
                                style={{ background: "linear-gradient(90deg, #6B21A8, #F97316)" }}>
                                Terminée
                            </span>
                        </div>

                        <div className="mb-5">
                            <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <UsersIcon className="w-5 h-5 text-purple-600" />Participants
                            </h4>
                            <div className="flex flex-wrap gap-2">
                                {reunionSelectionnee.participants.map((p, i) => (
                                    <span key={i} className="bg-purple-100 text-purple-700 text-sm px-3 py-1 rounded-full font-medium">{p}</span>
                                ))}
                            </div>
                        </div>

                        <div className="mb-5">
                            <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <ClipboardDocumentListIcon className="w-5 h-5 text-purple-600" />Ordres du jour
                            </h4>
                            <div className="bg-gray-50 p-4 rounded-xl space-y-2">
                                {reunionSelectionnee.ordresduJour && reunionSelectionnee.ordresduJour.length > 0 ? (
                                    reunionSelectionnee.ordresduJour.map((odj, i) => (
                                        <p key={i} className="text-gray-600 text-sm">{odj}</p>
                                    ))
                                ) : (
                                    <p className="text-gray-400 text-sm">Non défini</p>
                                )}
                            </div>
                        </div>

                        <div className="mb-6">
                            <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <SparklesIcon className="w-5 h-5 text-purple-600" />Transcription IA
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badgeTranscription(reunionSelectionnee.statutTranscription)}`}>
                                    {labelTranscription(reunionSelectionnee.statutTranscription)}
                                </span>
                            </h4>
                            <div className="bg-gray-50 p-4 rounded-xl max-h-48 overflow-y-auto border border-gray-100">
                                {reunionSelectionnee.statutTranscription === "en_cours" ? (
                                    <div className="flex items-center gap-2 text-orange-500">
                                        <Spinner size="sm" />
                                        <span className="text-sm">Transcription en cours...</span>
                                    </div>
                                ) : (
                                    <p className="text-gray-600 text-sm whitespace-pre-wrap leading-relaxed">
                                        {reunionSelectionnee.transcription || "Transcription non disponible — uploadez un audio"}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="flex gap-3 mb-4">
                            <button
                                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border-2 border-purple-200 text-purple-600 hover:bg-purple-50 transition-colors disabled:opacity-40"
                                onClick={() => telechargerAudio(reunionSelectionnee.urlAudio, reunionSelectionnee.titre)}
                                disabled={!reunionSelectionnee.urlAudio}>
                                <MusicalNoteIcon className="w-4 h-4" />Télécharger l'audio
                            </button>
                            <button
                                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border-2 border-green-200 text-green-600 hover:bg-green-50 transition-colors disabled:opacity-40"
                                onClick={() => telechargerCRC(reunionSelectionnee.id, reunionSelectionnee.titre)}
                                disabled={telechargementCRC === reunionSelectionnee.id}>
                                {telechargementCRC === reunionSelectionnee.id
                                    ? <><Spinner size="sm" />Génération...</>
                                    : <><DocumentTextIcon className="w-4 h-4" />Télécharger le CRC</>}
                            </button>
                        </div>

                        <div className="flex justify-between items-center border-t border-gray-100 pt-4 mt-4">
                            <button className="btn btn-sm rounded-xl text-red-500 border-red-200 hover:bg-red-50 gap-1"
                                onClick={() => supprimerReunion(reunionSelectionnee)}>
                                <TrashIcon className="w-4 h-4" />Supprimer
                            </button>
                            <button className="btn btn-sm rounded-xl"
                                onClick={() => setReunionSelectionnee(null)}>Fermer</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
